import { Skeleton } from "@mui/material";

const skeletonTone = {
  bgcolor: "var(--skeleton-bg, rgba(0, 0, 0, 0.06))",
};

/**
 * Use CSS pixel strings — MUI multiplies numeric borderRadius by
 * theme.shape.borderRadius (8 here), so `12` becomes 96px and looks pill-like.
 * Match Tailwind: rounded-lg 8 / rounded-xl 12 / rounded-2xl 16.
 */
const RADIUS = {
  bar: "4px",
  control: "8px",
  card: "12px",
  tile: "16px",
};

const skeletonBar = {
  borderRadius: RADIUS.bar,
  transform: "none",
  ...skeletonTone,
};

const skeletonControl = {
  borderRadius: RADIUS.control,
  transform: "none",
  ...skeletonTone,
};

const skeletonCard = {
  borderRadius: RADIUS.card,
  transform: "none",
  ...skeletonTone,
};

const skeletonTile = {
  borderRadius: RADIUS.tile,
  transform: "none",
  ...skeletonTone,
};

export const SummaryCardSkeleton = ({ withFooter = true } = {}) => (
  <div className="summary-card">
    <div className="flex items-center gap-2">
      <Skeleton
        variant="rounded"
        width={32}
        height={32}
        sx={{ ...skeletonTone, borderRadius: RADIUS.control, flexShrink: 0 }}
      />
      <Skeleton variant="text" width={110} height={16} />
    </div>
    <Skeleton variant="text" width="55%" height={28} sx={{ mt: 1.5 }} />
    {withFooter ? (
      <Skeleton variant="text" width={72} height={14} sx={{ mt: 1 }} />
    ) : null}
  </div>
);

export const TransactionRowSkeleton = () => (
  <div className="flex flex-col gap-2 border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border sm:flex-row sm:items-center sm:justify-between">
    <div className="flex min-w-0 items-center gap-3">
      <Skeleton variant="circular" width={36} height={36} sx={{ ...skeletonTone, flexShrink: 0 }} />
      <div className="min-w-0 space-y-1.5">
        <Skeleton variant="rounded" width={180} height={14} sx={skeletonBar} />
        <Skeleton variant="rounded" width={48} height={12} sx={skeletonBar} />
      </div>
    </div>
    <Skeleton variant="rounded" width={72} height={14} sx={skeletonBar} />
  </div>
);

const BalanceRowSkeleton = () => (
  <div className="flex flex-col gap-2 border-b border-gray-100 bg-gray-50/80 px-4 py-3 dark:border-brand-dark-border dark:bg-brand-dark-elevated/60 sm:flex-row sm:items-center sm:justify-between">
    <div className="flex min-w-0 items-center gap-3">
      <Skeleton variant="circular" width={36} height={36} sx={{ ...skeletonTone, flexShrink: 0 }} />
      <Skeleton variant="rounded" width={150} height={14} sx={skeletonBar} />
    </div>
    <Skeleton variant="rounded" width={88} height={14} sx={skeletonBar} />
  </div>
);

export const FiltersSkeleton = () => (
  <div className="mb-5 flex flex-wrap gap-3 rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3">
    <Skeleton variant="rounded" width={112} height={36} sx={skeletonControl} />
    <Skeleton variant="rounded" width={148} height={36} sx={skeletonControl} />
    <Skeleton variant="rounded" width={148} height={36} sx={skeletonControl} />
  </div>
);

export const DateFilterSkeleton = () => (
  <div className="flex flex-wrap gap-3 border-b border-gray-100 px-4 pb-4 pt-3">
    {Array.from({ length: 5 }).map((_, index) => (
      <Skeleton
        key={index}
        variant="rounded"
        width={index === 0 ? 72 : 64}
        height={36}
        sx={skeletonControl}
      />
    ))}
  </div>
);

export const TransactionListSkeleton = ({ rows = 10, withBalanceRows = false }) => (
  <div className="w-full overflow-hidden pb-2">
    {withBalanceRows && (
      <div className="px-4 py-2.5">
        <Skeleton variant="rounded" width={120} height={12} sx={skeletonBar} />
      </div>
    )}
    {withBalanceRows && <BalanceRowSkeleton />}
    {Array.from({ length: rows }).map((_, index) => (
      <TransactionRowSkeleton key={index} />
    ))}
    {withBalanceRows && <BalanceRowSkeleton />}
    <div className="flex justify-center px-4 pt-2 pb-1">
      <Skeleton variant="rounded" width={260} height={32} sx={skeletonBar} />
    </div>
  </div>
);

export const ExtractSkeleton = () => (
  <div className="w-full">
    <section className="mb-6">
      <div className="mb-3 flex items-center justify-between">
        <Skeleton variant="text" width={52} height={14} />
        <div className="flex gap-2">
          <Skeleton variant="rounded" width={36} height={36} sx={skeletonControl} />
          <Skeleton variant="rounded" width={128} height={36} sx={skeletonControl} />
        </div>
      </div>
      <div className="dashboard-card-grid">
        {Array.from({ length: 4 }).map((_, index) => (
          <SummaryCardSkeleton key={index} withFooter={false} />
        ))}
      </div>
    </section>

    <Skeleton variant="text" width={320} height={18} sx={{ mb: 2 }} />
    <div className="mb-4 flex gap-0 overflow-hidden rounded-lg border border-gray-100">
      {Array.from({ length: 4 }).map((_, index) => (
        <Skeleton
          key={index}
          variant="rectangular"
          height={44}
          sx={{ flex: 1, borderRadius: 0 }}
        />
      ))}
    </div>

    <section className="mb-5 min-w-0 overflow-hidden">
      <Skeleton variant="text" width={96} height={14} sx={{ mb: 3 }} />
      <div className="flex gap-3 overflow-hidden">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rounded"
            width={112}
            height={112}
            sx={{ ...skeletonTile, flexShrink: 0 }}
          />
        ))}
      </div>
    </section>

    <FiltersSkeleton />
    <div className="app-card w-full">
      <DateFilterSkeleton />
      <TransactionListSkeleton rows={12} withBalanceRows />
    </div>
  </div>
);

export const PixAreaSkeleton = () => (
  <div className="w-full">
    <div className="mb-3 flex items-center justify-between">
      <Skeleton variant="text" width={120} height={18} />
      <Skeleton variant="text" width={64} height={14} />
    </div>
    <FiltersSkeleton />
    <div className="mb-4 mt-4 dashboard-card-grid dashboard-card-grid-pix">
      {Array.from({ length: 4 }).map((_, index) => (
        <SummaryCardSkeleton key={index} withFooter={false} />
      ))}
    </div>
    <TransactionListSkeleton rows={4} />
  </div>
);

export const PixCardsSkeleton = ({ count = 3 }) => (
  <div className="mb-5 flex w-full min-w-0 max-w-full flex-col gap-3">
    <Skeleton variant="text" width={110} height={18} />
    <div className="grid w-full min-w-0 grid-cols-1 gap-3 lg:grid-cols-2">
      <Skeleton variant="rounded" height={220} sx={{ ...skeletonCard, width: "100%" }} />
      <Skeleton variant="rounded" height={220} sx={{ ...skeletonCard, width: "100%" }} />
    </div>
    <div className="dashboard-card-grid dashboard-card-grid-pix">
      {Array.from({ length: count }).map((_, index) => (
        <SummaryCardSkeleton key={index} />
      ))}
    </div>
  </div>
);

export const PayrollStatusCardSkeleton = () => (
  <div className="summary-card min-h-[88px]">
    <div className="flex items-center justify-between">
      <Skeleton variant="text" width={90} height={16} />
      <Skeleton variant="circular" width={20} height={20} />
    </div>
    <Skeleton variant="text" width={48} height={28} sx={{ mt: 1.5 }} />
  </div>
);

export const PayrollTableSkeleton = ({ rows = 5 }) => (
  <div className="app-card w-full overflow-hidden">
    <div className="border-b border-gray-100 px-4 py-3">
      <Skeleton variant="rounded" width={96} height={36} />
    </div>
    <div className="hidden gap-3 border-b border-gray-100 px-4 py-2 md:grid md:grid-cols-6">
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton key={index} variant="text" height={14} />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, index) => (
      <div
        key={index}
        className="grid grid-cols-[28px_100px_1fr_110px] items-center gap-x-4 border-b border-gray-100 px-4 py-3 md:grid-cols-[28px_120px_140px_1fr_110px]"
      >
        <Skeleton variant="circular" width={28} height={28} sx={{ flexShrink: 0 }} />
        <Skeleton variant="rounded" width={90} height={14} sx={skeletonBar} />
        <Skeleton
          variant="rounded"
          width={120}
          height={14}
          sx={{ ...skeletonBar, display: { xs: "none", md: "block" } }}
        />
        <Skeleton variant="rounded" width={140} height={14} sx={skeletonBar} />
        <Skeleton variant="rounded" width={110} height={24} sx={{ ...skeletonBar, justifySelf: "end" }} />
      </div>
    ))}
    <div className="flex justify-end px-2 py-2">
      <Skeleton variant="rounded" width={280} height={40} />
    </div>
  </div>
);

export const PayrollSkeleton = () => (
  <div className="w-full">
    <div className="app-card mb-6 p-4 md:p-5">
      <Skeleton variant="text" width={260} height={24} />
      <Skeleton variant="text" width="90%" height={16} sx={{ mt: 1 }} />
      <Skeleton variant="text" width="75%" height={16} />
      <div className="mt-4 flex flex-wrap gap-2">
        <Skeleton variant="rounded" width={160} height={36} />
        <Skeleton variant="rounded" width={168} height={36} />
      </div>
      <div className="mt-3 flex flex-wrap gap-3">
        <Skeleton variant="text" width={140} height={14} />
        <Skeleton variant="text" width={320} height={14} />
      </div>
    </div>

    <div className="mb-4">
      <Skeleton variant="text" width={180} height={24} />
      <Skeleton variant="text" width="85%" height={16} sx={{ mt: 1 }} />
    </div>

    <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <PayrollStatusCardSkeleton key={index} />
      ))}
    </div>

    <div className="mb-3">
      <Skeleton variant="text" width={220} height={24} />
    </div>

    <PayrollTableSkeleton rows={5} />
  </div>
);

export const TransferRowSkeleton = () => (
  <div className="flex flex-col gap-2 border-b border-gray-100 px-4 py-2.5 dark:border-brand-dark-border lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)] lg:items-center lg:gap-x-4">
    <div className="min-w-0 space-y-1.5">
      <Skeleton variant="rounded" width={160} height={14} sx={skeletonBar} />
      <Skeleton variant="rounded" width={120} height={12} sx={skeletonBar} />
    </div>
    <Skeleton variant="rounded" width={110} height={12} sx={skeletonBar} />
    <div className="min-w-0 space-y-1.5">
      <Skeleton variant="rounded" width={140} height={14} sx={skeletonBar} />
      <Skeleton variant="rounded" width={100} height={12} sx={skeletonBar} />
    </div>
    <Skeleton
      variant="rounded"
      width={88}
      height={14}
      sx={{ ...skeletonBar, justifySelf: { lg: "end" } }}
    />
  </div>
);

export const TransfersListSkeleton = ({ rows = 8 }) => (
  <div className="w-full pb-2 pt-1">
    {Array.from({ length: rows }).map((_, index) => (
      <TransferRowSkeleton key={index} />
    ))}
  </div>
);

export const TransfersAreaSkeleton = () => (
  <div className="w-full">
    <div className="mb-3">
      <Skeleton variant="text" width={88} height={20} />
    </div>
    <FiltersSkeleton />
    <div className="app-card mt-3 w-full">
      <DateFilterSkeleton />
      <TransfersListSkeleton rows={4} />
    </div>
  </div>
);

export const DashboardSkeleton = () => (
  <div className="dashboard-page w-full min-w-0 max-w-full">
    <div className="mb-6">
      <Skeleton variant="text" width={300} height={34} />
      <Skeleton variant="text" width={380} height={18} sx={{ mt: 0.5 }} />
    </div>

    <section className="mb-6">
      <div className="mb-3 flex items-center justify-between">
        <Skeleton variant="text" width={52} height={14} />
        <div className="flex gap-2">
          <Skeleton variant="rounded" width={36} height={36} sx={skeletonControl} />
          <Skeleton variant="rounded" width={128} height={36} sx={skeletonControl} />
        </div>
      </div>
      <div className="dashboard-card-grid">
        {Array.from({ length: 4 }).map((_, index) => (
          <SummaryCardSkeleton key={index} withFooter={false} />
        ))}
      </div>
    </section>

    <section className="mb-5 min-w-0 overflow-hidden">
      <Skeleton variant="text" width={96} height={14} sx={{ mb: 3 }} />
      <div className="flex gap-3 overflow-hidden">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rounded"
            width={112}
            height={112}
            sx={{ ...skeletonTile, flexShrink: 0 }}
          />
        ))}
      </div>
    </section>

    <PixCardsSkeleton count={3} />

    <section className="mb-5">
      <div className="mb-3">
        <Skeleton variant="text" width={150} height={18} />
        <Skeleton variant="text" width={120} height={14} sx={{ mt: 0.5 }} />
      </div>
      <FiltersSkeleton />
      <div className="mb-4 mt-4 dashboard-card-grid dashboard-card-grid-pix">
        {Array.from({ length: 4 }).map((_, index) => (
          <SummaryCardSkeleton key={index} withFooter={false} />
        ))}
      </div>
      <TransactionListSkeleton rows={4} />
    </section>
  </div>
);

export const PaymentBarcodeSkeleton = () => (
  <div className="app-card mt-5 w-full p-4 md:p-5">
    <Skeleton variant="text" width={160} height={20} />
    <Skeleton variant="text" width={120} height={36} sx={{ mt: 2 }} />
    <Skeleton variant="text" width={220} height={14} sx={{ mt: 1 }} />
    <div className="mt-5 space-y-3 border-t border-gray-100 pt-5">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="grid grid-cols-2 gap-3">
          <Skeleton variant="rounded" width={100} height={12} sx={skeletonBar} />
          <Skeleton variant="rounded" width={140} height={12} sx={skeletonBar} />
        </div>
      ))}
    </div>
  </div>
);

export const OrganizersSkeleton = () => (
  <div className="w-full space-y-5">
    <div className="app-card p-4">
      <Skeleton variant="rounded" width={180} height={36} sx={skeletonControl} />
    </div>
    <div className="app-card overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-3 sm:flex-row sm:items-center">
        <Skeleton variant="rounded" height={40} sx={{ ...skeletonControl, flex: 1 }} />
        <Skeleton variant="rounded" width={160} height={36} sx={skeletonControl} />
      </div>
      <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <SummaryCardSkeleton key={index} withFooter />
        ))}
      </div>
    </div>
  </div>
);

export const OperatorsSkeleton = () => (
  <div className="w-full space-y-5">
    <div className="app-card p-4">
      <Skeleton variant="rounded" width={180} height={36} sx={skeletonControl} />
    </div>
    <div className="app-card overflow-hidden">
      <div className="border-b border-gray-100 px-4 py-3">
        <Skeleton variant="text" width={180} height={20} />
        <Skeleton variant="text" width="70%" height={14} sx={{ mt: 0.5 }} />
      </div>
      <div className="hidden gap-3 border-b border-gray-100 px-4 py-2 md:grid md:grid-cols-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} variant="text" height={14} />
        ))}
      </div>
      {Array.from({ length: 5 }).map((_, index) => (
        <div
          key={index}
          className="grid grid-cols-1 gap-3 border-b border-gray-100 px-4 py-3 md:grid-cols-[1fr_1fr_100px_100px_40px] md:items-center"
        >
          <Skeleton variant="rounded" width="80%" height={14} sx={skeletonBar} />
          <Skeleton variant="rounded" width="90%" height={14} sx={skeletonBar} />
          <Skeleton variant="rounded" width={80} height={14} sx={skeletonBar} />
          <Skeleton variant="rounded" width={72} height={22} sx={skeletonBar} />
          <Skeleton variant="circular" width={32} height={32} />
        </div>
      ))}
    </div>
  </div>
);

export const PaymentConfirmSkeleton = () => (
  <div className="app-card w-full p-4 md:p-5">
    <Skeleton variant="text" width={160} height={20} />
    <Skeleton variant="text" width={120} height={36} sx={{ mt: 2 }} />
    <Skeleton variant="text" width={220} height={14} sx={{ mt: 1 }} />
    <div className="mt-5 space-y-3 border-t border-gray-100 pt-5">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="grid grid-cols-2 gap-3">
          <Skeleton variant="rounded" width={110} height={12} sx={skeletonBar} />
          <Skeleton variant="rounded" width={150} height={12} sx={skeletonBar} />
        </div>
      ))}
    </div>
    <Skeleton variant="rounded" width={200} height={44} sx={{ ...skeletonBar, mt: 4 }} />
  </div>
);

export const StablesBalanceValueSkeleton = () => (
  <>
    <Skeleton variant="text" width="55%" height={28} sx={{ mt: 2 }} />
    <Skeleton variant="text" width={72} height={14} sx={{ mt: 1 }} />
  </>
);

export const StablesBalanceCardsSkeleton = ({ count = 5 } = {}) => (
  <div className="grid w-full min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-[repeat(auto-fit,minmax(11.5rem,1fr))]">
    {Array.from({ length: count }).map((_, index) => (
      <SummaryCardSkeleton key={index} />
    ))}
  </div>
);

export const StablesQuoteCarouselSkeleton = ({ count = 3 } = {}) => (
  <div className="quotes-carousel-track flex-1">
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className="quote-carousel-item quote-carousel-item--stables">
        <Skeleton variant="circular" width={30} height={30} sx={{ ...skeletonTone, flexShrink: 0 }} />
        <div className="min-w-0 flex-1 space-y-1">
          <Skeleton variant="rounded" width={56} height={11} sx={skeletonBar} />
          <Skeleton variant="rounded" width={88} height={14} sx={skeletonBar} />
        </div>
      </div>
    ))}
  </div>
);

export const StablesInlineValueSkeleton = ({ width = 120, height = 24 } = {}) => (
  <Skeleton variant="rounded" width={width} height={height} sx={{ ...skeletonBar, mt: 1 }} />
);

export const StablesOperationsPanelSkeleton = () => (
  <div className="app-card overflow-hidden">
    <div className="grid gap-5 p-4 md:grid-cols-2 md:p-5">
      <div className="space-y-4">
        <div>
          <Skeleton variant="text" width={80} height={12} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={44} sx={skeletonBar} />
        </div>
        <Skeleton variant="rounded" height={112} sx={skeletonBar} />
        <div>
          <Skeleton variant="text" width={100} height={12} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={44} sx={skeletonBar} />
        </div>
        <div>
          <Skeleton variant="text" width={140} height={11} sx={{ mb: 1 }} />
          <StablesInlineValueSkeleton width={100} height={20} />
        </div>
        <Skeleton variant="rounded" width={180} height={40} sx={skeletonBar} />
      </div>
      <div className="space-y-3 rounded-xl border border-gray-100 p-4">
        <Skeleton variant="text" width={140} height={12} />
        <Skeleton variant="rounded" height={160} sx={skeletonBar} />
      </div>
    </div>
  </div>
);

const StablesHistoryRowSkeleton = () => (
  <div className="col-span-4 flex flex-col gap-2 border-b border-gray-100 px-4 py-2.5 last:border-b-0 dark:border-brand-dark-border lg:grid lg:grid-cols-subgrid lg:items-center lg:gap-x-4 lg:px-0 lg:py-2">
    <div className="flex min-w-0 items-center gap-3">
      <Skeleton variant="circular" width={36} height={36} sx={{ ...skeletonTone, flexShrink: 0 }} />
      <Skeleton variant="rounded" width={160} height={14} sx={skeletonBar} />
    </div>
    <Skeleton variant="rounded" width={80} height={12} sx={skeletonBar} />
    <Skeleton variant="rounded" width={72} height={14} sx={{ ...skeletonBar, ml: { xs: 0, lg: "auto" } }} />
    <div className="flex items-center justify-between gap-2 lg:justify-end">
      <Skeleton variant="rounded" width={72} height={22} sx={skeletonBar} />
      <Skeleton variant="circular" width={28} height={28} sx={skeletonTone} />
    </div>
  </div>
);

export const StablesHistoryListSkeleton = ({ rows = 8 } = {}) => (
  <div className="lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)] lg:gap-x-4 lg:px-4">
    {Array.from({ length: rows }).map((_, index) => (
      <StablesHistoryRowSkeleton key={index} />
    ))}
  </div>
);

export const StablesPriceMiniChartSkeleton = () => (
  <div className="stables-inset-panel p-3">
    <div className="flex items-start justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2">
        <Skeleton variant="circular" width={32} height={32} sx={{ ...skeletonTone, flexShrink: 0 }} />
        <div className="min-w-0 space-y-1">
          <Skeleton variant="rounded" width={64} height={11} sx={skeletonBar} />
          <Skeleton variant="rounded" width={88} height={16} sx={skeletonBar} />
          <Skeleton variant="rounded" width={96} height={10} sx={skeletonBar} />
        </div>
      </div>
      <Skeleton variant="rounded" width={28} height={20} sx={skeletonBar} />
    </div>
    <Skeleton variant="rounded" height={56} sx={{ ...skeletonBar, mt: 2, width: "100%" }} />
  </div>
);

export const StablesLimitCategoryCardSkeleton = () => (
  <div className="summary-card min-w-0 w-full">
    <div className="flex items-start gap-2">
      <Skeleton variant="rounded" width={32} height={32} sx={skeletonTone} />
      <div className="min-w-0 flex-1 space-y-1">
        <Skeleton variant="text" width={120} height={16} />
        <Skeleton variant="text" width="80%" height={12} />
      </div>
    </div>
    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index}>
          <Skeleton variant="text" width={56} height={11} />
          <Skeleton variant="text" width={80} height={16} sx={{ mt: 0.5 }} />
        </div>
      ))}
    </div>
    <Skeleton variant="rounded" height={8} sx={{ ...skeletonBar, mt: 4, width: "100%" }} />
    <Skeleton variant="text" width={140} height={12} sx={{ mt: 1 }} />
  </div>
);

export const StablesLimitsSkeleton = () => (
  <>
    <div className="dashboard-card-grid dashboard-card-grid-pix mb-5">
      {Array.from({ length: 3 }).map((_, index) => (
        <SummaryCardSkeleton key={index} />
      ))}
    </div>
    <div className="app-card overflow-hidden">
      <div className="border-b border-gray-100 px-4 py-3">
        <Skeleton variant="text" width={180} height={18} />
        <Skeleton variant="text" width="70%" height={12} sx={{ mt: 0.5 }} />
      </div>
      <div className="grid gap-3 p-4 md:grid-cols-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <StablesLimitCategoryCardSkeleton key={index} />
        ))}
      </div>
    </div>
  </>
);

export const StablesWithdrawLimitBarSkeleton = () => (
  <section className="app-card mb-6 overflow-hidden p-4">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1 space-y-1">
        <Skeleton variant="text" width={160} height={18} />
        <Skeleton variant="text" width="75%" height={12} />
      </div>
      <Skeleton variant="rounded" width={88} height={14} sx={skeletonBar} />
    </div>
    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index}>
          <Skeleton variant="text" width={56} height={11} />
          <Skeleton variant="text" width={80} height={16} sx={{ mt: 0.5 }} />
        </div>
      ))}
    </div>
    <Skeleton variant="rounded" height={8} sx={{ ...skeletonBar, mt: 4, width: "100%" }} />
    <Skeleton variant="text" width={220} height={12} sx={{ mt: 1 }} />
  </section>
);

export const StablesDepositSkeleton = () => (
  <div className="space-y-4">
    <Skeleton variant="text" width="90%" height={14} />
    <div>
      <Skeleton variant="text" width={48} height={12} sx={{ mb: 1 }} />
      <Skeleton variant="rounded" height={44} sx={skeletonBar} />
    </div>
    <div>
      <Skeleton variant="text" width={40} height={12} sx={{ mb: 1 }} />
      <Skeleton variant="rounded" height={44} sx={skeletonBar} />
    </div>
    <div className="stables-deposit-panel mt-4 p-4">
      <div className="mb-4 flex flex-wrap justify-between gap-2">
        <Skeleton variant="text" width={100} height={32} />
        <Skeleton variant="text" width={72} height={32} />
      </div>
      <div className="grid gap-6 md:grid-cols-[200px_1fr]">
        <Skeleton variant="rounded" height={200} sx={skeletonBar} />
        <div className="space-y-2">
          <Skeleton variant="text" width={140} height={12} />
          <Skeleton variant="rounded" height={40} sx={skeletonBar} />
        </div>
      </div>
    </div>
  </div>
);
